import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  Globe,
  Terminal,
  Hash,
  Send,
  GitBranch,
  ExternalLink,
  MessageSquare,
  FileText,
  XCircle,
  PlayCircle,
  Ban,
  Trash2,
  CheckCircle,
  AlertTriangle,
  Pause,
} from 'lucide-react';
import type { DashboardRunResponse } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDuration } from '@/lib/format';
import { useWorkflowStore } from '@/stores/workflow-store';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { runStatusMeta } from '@/lib/workflow-status';
import type { WorkflowState } from '@/lib/types';

interface WorkflowRunCardProps {
  run: DashboardRunResponse;
  isDocker?: boolean;
  onCancel: (runId: string) => void;
  onResume?: (runId: string) => void;
  onAbandon?: (runId: string) => void;
  onDelete?: (runId: string) => void;
  onApprove?: (runId: string) => void;
  onReject?: (runId: string) => void;
}

const PLATFORM_ICONS: Record<string, React.ReactElement> = {
  web: <Globe className="h-3.5 w-3.5" />,
  cli: <Terminal className="h-3.5 w-3.5" />,
  slack: <Hash className="h-3.5 w-3.5" />,
  telegram: <Send className="h-3.5 w-3.5" />,
  github: <GitBranch className="h-3.5 w-3.5" />,
};

function StepProgress({
  run,
  liveState,
}: {
  run: DashboardRunResponse;
  liveState: WorkflowState | undefined;
}): React.ReactElement | null {
  const dagNodes = liveState?.dagNodes ?? [];
  const runningNode = dagNodes
    .slice()
    .reverse()
    .find(n => n.status === 'running');
  const completedCount = dagNodes.filter(n => n.status === 'completed').length;
  const totalNodes = dagNodes.length || run.total_steps || 0;
  const stepName = runningNode?.name ?? run.current_step_name;
  const currentTool = liveState?.currentTool ?? null;

  const hasProgress = runningNode != null || totalNodes > 0;
  if (!hasProgress && !currentTool) return null;

  return (
    <div className="space-y-1 rounded-xl border border-border/60 bg-background/80 px-3 py-2 shadow-sm">
      {hasProgress && (
        <div className="flex items-center gap-2 text-sm text-text-primary">
          <span className="font-medium">
            {`${String(completedCount)}${totalNodes ? `/${String(totalNodes)}` : ''} nodes`}
          </span>
          {stepName && <span className="text-text-secondary">{stepName}</span>}
        </div>
      )}
      {currentTool && (
        <div className="flex items-center gap-2">
          {currentTool.status === 'running' && (
            <div className="h-3.5 w-3.5 shrink-0 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          )}
          <span
            className={cn(
              'text-sm font-mono truncate',
              currentTool.status === 'running' ? 'text-primary' : 'text-text-secondary'
            )}
          >
            {currentTool.status === 'running'
              ? currentTool.name
              : `${currentTool.name} (${currentTool.durationMs ? `${(currentTool.durationMs / 1000).toFixed(1)}s` : 'done'})`}
          </span>
        </div>
      )}
    </div>
  );
}

interface NodeCounts {
  completed: number;
  failed: number;
  skipped: number;
  total: number;
}

function isValidNodeCounts(value: unknown): value is NodeCounts {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.completed === 'number' &&
    typeof obj.failed === 'number' &&
    typeof obj.skipped === 'number' &&
    typeof obj.total === 'number'
  );
}

function NodeCountsSummary({ counts }: { counts: NodeCounts }): React.ReactElement {
  const hasFailures = counts.failed > 0 || counts.skipped > 0;
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {hasFailures ? (
        <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0" />
      ) : (
        <CheckCircle className="h-3.5 w-3.5 text-success shrink-0" />
      )}
      <span className={hasFailures ? 'text-warning' : 'text-success'}>
        {String(counts.completed)}/{String(counts.total)} nodes succeeded
      </span>
      {counts.failed > 0 && (
        <span className="text-text-secondary">&middot; {String(counts.failed)} failed</span>
      )}
      {counts.skipped > 0 && (
        <span className="text-text-secondary">&middot; {String(counts.skipped)} skipped</span>
      )}
    </div>
  );
}

export function WorkflowRunCard({
  run,
  isDocker,
  onCancel,
  onResume,
  onAbandon,
  onDelete,
  onApprove,
  onReject,
}: WorkflowRunCardProps): React.ReactElement {
  const navigate = useNavigate();
  const { confirm, confirmDialog } = useConfirm();
  const [elapsed, setElapsed] = useState(() => formatDuration(run.started_at, run.completed_at));

  // Live SSE state from Zustand store — overrides REST-polled data when present
  const liveState = useWorkflowStore(state => state.workflows.get(run.id));

  useEffect(() => {
    if (run.status !== 'running' && run.status !== 'paused') return;
    const interval = setInterval(() => {
      setElapsed(formatDuration(run.started_at, null));
    }, 1000);
    return (): void => {
      clearInterval(interval);
    };
  }, [run.status, run.started_at]);

  const chatId = run.parent_platform_id ?? run.worker_platform_id;
  const [messageExpanded, setMessageExpanded] = useState(false);
  const longMessage = (run.user_message?.length ?? 0) > 80;
  const displayMessage = run.user_message
    ? messageExpanded || !longMessage
      ? run.user_message
      : run.user_message.slice(0, 80) + '…'
    : null;

  const statusMeta = runStatusMeta(run.status);

  return (
    <>
      <div className="space-y-4 rounded-[22px] border border-border/70 bg-surface/95 p-4 shadow-sm transition-shadow hover:shadow-md">
        {/* Header: status dot + name + badge + elapsed */}
        <div className="flex items-start gap-3">
          <div className={cn('mt-1 h-2.5 w-2.5 shrink-0 rounded-full', statusMeta.dotClass)} />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
                  {run.codebase_name ?? 'Workflow Run'}
                </div>
                <span className="block truncate text-sm font-semibold text-text-primary">
                  {run.workflow_name}
                </span>
              </div>
              <span className="text-xs text-text-tertiary shrink-0">{elapsed}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                  statusMeta.badgeClass
                )}
              >
                {run.status}
              </span>
              <span className="text-[11px] text-text-tertiary">
                {run.platform_type ?? 'unknown'} platform
              </span>
            </div>
          </div>
        </div>

        {/* Live progress */}
        <StepProgress run={run} liveState={liveState} />

        {/* Node outcome summary for completed/failed runs */}
        {(run.status === 'completed' || run.status === 'failed') &&
          isValidNodeCounts(run.metadata?.node_counts) && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border/60 bg-background/70 px-3 py-2">
              <NodeCountsSummary counts={run.metadata.node_counts} />
              {typeof run.metadata?.total_cost_usd === 'number' && (
                <span className="text-xs text-text-secondary">
                  ${run.metadata.total_cost_usd.toFixed(4)} USD
                </span>
              )}
            </div>
          )}

        {/* Metadata row */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-text-secondary">
          <span className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/70 px-2.5 py-1">
            {PLATFORM_ICONS[run.platform_type ?? ''] ?? <Globe className="h-3.5 w-3.5" />}
            {run.platform_type ?? 'unknown'}
          </span>
          <span className="rounded-full border border-border/60 bg-background/70 px-2.5 py-1">
            {run.codebase_name ?? 'Unknown project'}
          </span>
          {run.parent_platform_id && run.parent_platform_id !== run.worker_platform_id && (
            <button
              onClick={(): void => {
                navigate(`/chat/${encodeURIComponent(run.parent_platform_id ?? '')}`);
              }}
              className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/5 px-2.5 py-1 text-primary/80 transition-colors hover:text-primary"
            >
              <MessageSquare className="h-3 w-3" />
              Parent chat
            </button>
          )}
        </div>

        {/* User message — expandable */}
        {displayMessage && (
          <div className="space-y-1 rounded-xl border border-border/60 bg-background/70 px-3 py-2">
            <p className={cn('text-xs text-text-tertiary italic', !messageExpanded && 'truncate')}>
              {displayMessage}
            </p>
            {longMessage && (
              <button
                onClick={(): void => {
                  setMessageExpanded(e => !e);
                }}
                className="text-[10px] text-text-tertiary hover:text-text-secondary underline"
              >
                {messageExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        )}

        {/* Approval request message */}
        {run.status === 'paused' && run.metadata?.approval != null && (
          <div className="flex items-start gap-2 rounded-xl border border-warning/20 bg-warning/5 px-3 py-2">
            <Pause className="h-4 w-4 text-warning shrink-0 mt-0.5" />
            <p className="text-xs text-text-secondary">
              {(
                run.metadata.approval as {
                  message?: string;
                }
              )?.message ?? 'Waiting for approval'}
            </p>
          </div>
        )}

        {/* Working path */}
        {run.working_path && (
          <p className="rounded-xl border border-border/60 bg-background/70 px-3 py-2 text-[11px] text-text-tertiary truncate">
            Worktree: {run.working_path.split('/').pop()}
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 border-t border-border/60 pt-3">
          <button
            onClick={(): void => {
              navigate(`/workflows/runs/${run.id}`);
            }}
            className="flex items-center gap-1 rounded-xl border border-border/60 bg-background/70 px-3 py-1.5 text-xs text-text-secondary transition-colors hover:bg-surface-elevated hover:text-text-primary"
          >
            <FileText className="h-3.5 w-3.5" />
            View Logs
          </button>
          {chatId && (
            <button
              onClick={(): void => {
                navigate(`/chat/${encodeURIComponent(chatId)}`);
              }}
              className="flex items-center gap-1 rounded-xl border border-border/60 bg-background/70 px-3 py-1.5 text-xs text-text-secondary transition-colors hover:bg-surface-elevated hover:text-text-primary"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Open Chat
            </button>
          )}
          {run.working_path && !isDocker && (
            <a
              href={`vscode://file/${run.working_path.replace(/\\/g, '/')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden items-center gap-1 rounded-xl border border-border/60 bg-background/70 px-3 py-1.5 text-xs text-text-secondary transition-colors hover:bg-surface-elevated hover:text-text-primary md:flex"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open in IDE
            </a>
          )}
          <div className="ml-auto flex items-center gap-1">
            {run.status === 'paused' && onApprove && (
              <button
                onClick={(): void => {
                  onApprove(run.id);
                }}
                className="flex items-center gap-1 rounded-xl px-3 py-1.5 text-xs text-success/80 transition-colors hover:bg-success/10 hover:text-success"
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Approve
              </button>
            )}
            {run.status === 'paused' && onReject && (
              <button
                onClick={(): void => {
                  void confirm({
                    title: `Reject workflow "${run.workflow_name}"?`,
                    risk: 'moderate',
                    confirmLabel: 'Reject',
                  }).then(ok => {
                    if (ok) onReject(run.id);
                  });
                }}
                className="flex items-center gap-1 rounded-xl px-3 py-1.5 text-xs text-error/80 transition-colors hover:bg-error/10 hover:text-error"
              >
                <XCircle className="h-3.5 w-3.5" />
                Reject
              </button>
            )}
            {run.status === 'failed' && onResume && (
              <button
                onClick={(): void => {
                  onResume(run.id);
                }}
                className="flex items-center gap-1 rounded-xl px-3 py-1.5 text-xs text-primary/80 transition-colors hover:bg-primary/10 hover:text-primary"
              >
                <PlayCircle className="h-3.5 w-3.5" />
                Resume
              </button>
            )}
            {run.status === 'running' && onAbandon && (
              <button
                onClick={(): void => {
                  void confirm({
                    title: `Abandon workflow "${run.workflow_name}"?`,
                    description: 'The run is terminated and cannot be resumed.',
                    risk: 'high',
                    confirmLabel: 'Abandon',
                  }).then(ok => {
                    if (ok) onAbandon(run.id);
                  });
                }}
                className="flex items-center gap-1 rounded-xl px-3 py-1.5 text-xs text-warning/80 transition-colors hover:bg-warning/10 hover:text-warning"
              >
                <Ban className="h-3.5 w-3.5" />
                Abandon
              </button>
            )}
            {(run.status === 'running' || run.status === 'pending') && (
              <button
                onClick={(): void => {
                  void confirm({
                    title: `Cancel workflow "${run.workflow_name}"?`,
                    risk: 'high',
                    confirmLabel: 'Cancel run',
                    cancelLabel: 'Keep running',
                  }).then(ok => {
                    if (ok) onCancel(run.id);
                  });
                }}
                className="flex items-center gap-1 rounded-xl px-3 py-1.5 text-xs text-error/80 transition-colors hover:bg-error/10 hover:text-error"
              >
                <XCircle className="h-3.5 w-3.5" />
                Cancel
              </button>
            )}
            {onDelete && run.status !== 'running' && run.status !== 'pending' && (
              <button
                onClick={(): void => {
                  void confirm({
                    title: `Delete workflow run "${run.workflow_name}"?`,
                    description: 'This permanently deletes the run record and its logs.',
                    risk: 'high',
                    confirmLabel: 'Delete',
                  }).then(ok => {
                    if (ok) onDelete(run.id);
                  });
                }}
                className="flex items-center gap-1 rounded-xl px-3 py-1.5 text-xs text-text-tertiary transition-colors hover:bg-error/10 hover:text-error"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            )}
          </div>
        </div>
      </div>
      {confirmDialog}
    </>
  );
}
